import React from 'react'
import PropTypes from 'prop-types'
import ReactMarkdown from 'react-markdown'
import { cloneDeep } from 'lodash-es'
import { defaultSchema } from 'hast-util-sanitize'
import rehypeRaw from 'rehype-raw'
import rehypeSanitize from 'rehype-sanitize'

import { COLOR, FONT, GTR } from '@farewill/ui/tokens'
import { Wrapper } from '@farewill/ui'
import { screenMin } from '@farewill/ui/helpers/responsive'
import styled, { createGlobalStyle } from 'styled-components'
import DefaultLayout from 'layouts/DefaultLayout'
import { LIVE_PRODUCTS, PRODUCTS } from 'lib/products/constants'
import { StyledRichContentWrapper } from 'views/general/templates/components/RichContent'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const remarkHeadingId = require('remark-heading-id')

const GlobalStyles = createGlobalStyle`
  :root {
    scroll-behavior: smooth;
  }
`

const StyledWrapper = styled(Wrapper)`
  ol {
    list-style-type: number;
  }

  ul {
    list-style-type: disc;
  }

  li {
    margin-inline-start: 24px;

    p {
      margin: 0;
    }
  }

  li:last-of-type p {
    margin-bottom: 0;
  }

  // Table styles
  .table-wrapper {
    --container-padding: 24px;
    --fade-width: ${GTR.M};

    ${screenMin.m`
      --container-padding: 32px;
    `};

    ${screenMin.l`
      --container-padding: 48px;
    `};

    margin: 0 calc(-1 * var(--container-padding)) ${GTR.M};

    ul {
      margin: 0;
    }
  }

  .table-scroller-wrapper {
    padding: 0 var(--container-padding);
    overflow-x: auto;
    mask-image: linear-gradient(
      to right,
      transparent 0,
      black var(--fade-width),
      black calc(100% - var(--fade-width)),
      transparent 100%
    );
  }

  table {
    border-collapse: collapse;
    font-size: ${FONT.SIZE.S};
    width: 100%;
  }

  col:not(:first-of-type) {
    min-width: 26ch;
  }

  col:first-of-type {
    min-width: 15ch;
  }

  th {
    border-bottom: 1px solid ${COLOR.GREY.LIGHT};
  }

  tr:not(:last-of-type) {
    border-bottom: 1px solid ${COLOR.GREY.LIGHT};
  }

  th,
  td {
    padding-bottom: ${GTR.S};
    padding-top: ${GTR.S};
    text-align: start;
    vertical-align: baseline;
  }

  th:not(:first-of-type),
  td:not(:first-of-type) {
    padding-inline-start: ${GTR.S};
  }

  th:not(:last-of-type),
  td:not(:last-of-type) {
    padding-inline-end: ${GTR.S};
  }
`

/**
 * Clone the default rehype schema and add `className` to the list of allowed
 * attributes (required for the CookieYes cookie table to work).
 *
 * This should be safe as all policy content comes from the wills API endpoint
 * which serves the content from the `policies` MongoDB collection, which is
 * populated from Markdown files stored in the Wills API repo.
 *
 * Thus, the only way malicious content could make it into this markdown is
 * either:
 * 1. A nefarious actor has access to MongoDB which would allow them to update
 *    `content` field of the policy document stored in the database. If this
 *    happens we've got bigger issues than a potential markdown-induced
 *    vulnerability.
 * 2. A nefarious actor has committed (and merged) changes to the policy
 *    markdown files in Git. If the actor had such access they would likely make
 *    code changes directly to the application code itself, rather than by
 *    trying to embed malicious code in the policy content in Markdown.
 */
const schema = cloneDeep(defaultSchema)
schema.attributes['*'].push('className')

// Create a custom instance of rehype-sanitize with our custom schema.
const sanitizeInstance = rehypeSanitize(schema)

export type TermsProps = {
  pageContext: {
    policy: string
    title: string
    description: string
    compactHeader?: boolean
    funeralsFooter?: boolean
    product: PRODUCTS
  }
}

const Table = (
  props: React.ClassAttributes<HTMLTableElement> &
    React.TableHTMLAttributes<HTMLTableElement> & {
      node?: unknown
    }
): React.ReactElement => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { node, ...rest } = props
  return (
    <div className="table-wrapper">
      <div className="table-scroller-wrapper">
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <table {...rest} />
      </div>
    </div>
  )
}

const Terms = ({ pageContext }: TermsProps): React.ReactElement => (
  <DefaultLayout
    title={pageContext.title}
    description={pageContext.description}
    product={pageContext.product}
    headerType={pageContext?.compactHeader ? 'compact' : undefined}
    footerShowFuneralPlansLegalInformation={!!pageContext?.funeralsFooter}
  >
    <GlobalStyles />
    <StyledWrapper container tag={StyledRichContentWrapper}>
      <ReactMarkdown
        components={{
          table: Table,
        }}
        rehypePlugins={
          [rehypeRaw, sanitizeInstance] as ReactMarkdown.PluggableList
        }
        remarkPlugins={[remarkHeadingId]}
      >
        {pageContext.policy}
      </ReactMarkdown>
    </StyledWrapper>
  </DefaultLayout>
)

Terms.propTypes = {
  pageContext: PropTypes.shape({
    policy: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    product: PropTypes.oneOf(LIVE_PRODUCTS),
  }).isRequired,
}

export default Terms
